// src/data/translations.ts
// Pure translation map + t() helper + useT() React hook.
// Add more Georgian strings as needed — English always serves as fallback.

import { useLanguageStore } from '../stores/useLanguageStore';
import type { Lang } from '../stores/useLanguageStore';

export type TransKey =
  | 'NAV_ABOUT' | 'NAV_BOOKS' | 'NAV_PARTNERS' | 'NAV_CONTACT'
  | 'NAV_PREMIUM' | 'NAV_LOGIN' | 'NAV_SIGNUP'
  | 'HERO_BADGE' | 'HERO_TITLE_LINE1' | 'HERO_TITLE_LINE2'
  | 'HERO_SUBTITLE' | 'HERO_CTA1' | 'HERO_CTA2'
  | 'HERO_STAT1_LABEL' | 'HERO_STAT2_LABEL' | 'HERO_STAT3_LABEL'
  | 'BOOKS_HEADING' | 'BOOKS_AVAILABLE' | 'BOOKS_LOADING'
  | 'BOOKS_EMPTY' | 'BOOKS_ERROR'
  | 'UNIT_LOCKED' | 'UNIT_START' | 'UNIT_CONTINUE' | 'UNIT_REVIEW' | 'UNIT_FREE'
  | 'TEST_A' | 'TEST_B' | 'TEST_PROGRESS' | 'TEST_FINAL'
  | 'STREAK_UPSELL'
  // ── Footer ──────────────────────────────────────────────────────────────────
  | 'FOOTER_QUICK_LINKS'
  | 'FOOTER_CONTACT_TITLE'
  | 'FOOTER_DEV_TITLE'
  | 'FOOTER_LINK_LEARNING'
  | 'FOOTER_LINK_ABOUT'
  | 'FOOTER_LINK_PARTNERS'
  | 'FOOTER_LINK_PREMIUM'
  | 'FOOTER_LINK_CONTACT'
  | 'FOOTER_COPYRIGHT'
  | 'FOOTER_LEGAL_PRIVACY'
  | 'FOOTER_LEGAL_COOKIES'
  | 'FOOTER_LEGAL_TERMS'
  | 'FOOTER_LEGAL_ACCESSIBILITY'
  // ── Cookie consent banner ────────────────────────────────────────────────────
  | 'COOKIE_BANNER_TITLE'
  | 'COOKIE_BANNER_BODY'
  | 'COOKIE_BANNER_POLICY_LINK'
  | 'COOKIE_BANNER_ACCEPT'
  | 'COOKIE_BANNER_DECLINE';

const T: Record<TransKey, { en: string; ge: string }> = {
  // ── Navigation ──────────────────────────────────────────────────────────
  NAV_ABOUT:        { en: 'About',                         ge: 'შესახებ'                          },
  NAV_BOOKS:        { en: 'Books',                         ge: 'წიგნები'                          },
  NAV_PARTNERS:     { en: 'Partners',                      ge: 'პარტნიორები'                      },
  NAV_CONTACT:      { en: 'Contact',                       ge: 'კონტაქტი'                         },
  NAV_PREMIUM:      { en: 'Premium',                       ge: 'პრემიუმი'                         },
  NAV_LOGIN:        { en: 'Login',                         ge: 'შესვლა'                           },
  NAV_SIGNUP:       { en: 'Sign Up',                       ge: 'რეგისტრაცია'                      },

  // ── Hero ────────────────────────────────────────────────────────────────
  HERO_BADGE:       { en: 'English for Sports Professionals', ge: 'ინგლისური სპორტის პროფესიონალებისთვის' },
  HERO_TITLE_LINE1: { en: 'Master English.',               ge: 'დაეუფლე ინგლისურს.'               },
  HERO_TITLE_LINE2: { en: 'Elevate Your Sport.',           ge: 'ამაღლე შენი სპორტი.'              },
  HERO_SUBTITLE:    {
    en: 'Sports-focused English learning for athletes, coaches, and professionals who want to communicate with confidence.',
    ge: 'სპორტზე ორიენტირებული ინგლისური სპორტსმენებისთვის, მწვრთნელებისა და პროფესიონალებისთვის.',
  },
  HERO_CTA1:        { en: 'Continue Learning',             ge: 'სწავლის გაგრძელება'               },
  HERO_CTA2:        { en: 'Browse Books',                  ge: 'წიგნების დათვალიერება'             },
  HERO_STAT1_LABEL: { en: 'Books Available',               ge: 'ხელმისაწვდომი წიგნი'              },
  HERO_STAT2_LABEL: { en: 'Completed',                     ge: 'დასრულებული'                      },
  HERO_STAT3_LABEL: { en: 'Offline Ready',                 ge: 'ოფლაინ მზადაა'                    },

  // ── Books section ────────────────────────────────────────────────────────
  BOOKS_HEADING:    { en: 'Your Books',                    ge: 'შენი წიგნები'                     },
  BOOKS_AVAILABLE:  { en: 'available',                     ge: 'ხელმისაწვდომი'                    },
  BOOKS_LOADING:    { en: 'Loading books…',                ge: 'იტვირთება…'                       },
  BOOKS_EMPTY:      { en: 'No books available yet',        ge: 'წიგნები ჯერ არ არის'              },
  BOOKS_ERROR:      { en: 'Something went wrong',          ge: 'შეცდომა'                          },

  // ── Unit card ────────────────────────────────────────────────────────────
  UNIT_LOCKED:      { en: 'Locked',                        ge: 'დაბლოკილი'                        },
  UNIT_START:       { en: 'Start',                         ge: 'დაწყება'                          },
  UNIT_CONTINUE:    { en: 'Continue',                      ge: 'გაგრძელება'                       },
  UNIT_REVIEW:      { en: 'Review',                        ge: 'განხილვა'                         },
  UNIT_FREE:        { en: 'Free',                          ge: 'უფასო'                            },

  // ── Test buttons ─────────────────────────────────────────────────────────
  TEST_A:           { en: 'TEST A',                        ge: 'ტ. A'                             },
  TEST_B:           { en: 'TEST B',                        ge: 'ტ. B'                             },
  TEST_PROGRESS:    { en: 'PROGRESS',                      ge: 'პრ.'                              },
  TEST_FINAL:       { en: 'FINAL',                         ge: 'ბოლო'                             },

  // ── Streak ───────────────────────────────────────────────────────────────
  STREAK_UPSELL:    { en: 'Go Premium',                    ge: 'გადადი პრემიუმზე'                 },

  // ── Footer ───────────────────────────────────────────────────────────────
  FOOTER_QUICK_LINKS:         { en: 'Quick Links',         ge: 'სწრაფი ბმულები'                  },
  FOOTER_CONTACT_TITLE:       { en: 'Contact',             ge: 'კონტაქტი'                         },
  FOOTER_DEV_TITLE:           { en: 'Built By',            ge: 'შეიქმნა'                          },
  FOOTER_LINK_LEARNING:       { en: 'Learning Materials',  ge: 'სასწავლო მასალები'                },
  FOOTER_LINK_ABOUT:          { en: 'About Our Mission',   ge: 'ჩვენი მისიის შესახებ'             },
  FOOTER_LINK_PARTNERS:       { en: 'Partner Organizations', ge: 'პარტნიორი ორგანიზაციები'       },
  FOOTER_LINK_PREMIUM:        { en: 'Premium Access',      ge: 'პრემიუმ წვდომა'                   },
  FOOTER_LINK_CONTACT:        { en: 'Get in Touch',        ge: 'დაგვიკავშირდი'                    },
  FOOTER_COPYRIGHT:           { en: '© 2026 START. All rights reserved.', ge: '© 2026 START. ყველა უფლება დაცულია.' },
  FOOTER_LEGAL_PRIVACY:       { en: 'Privacy Policy',      ge: 'კონფიდენციალურობის პოლიტიკა'     },
  FOOTER_LEGAL_COOKIES:       { en: 'Cookies Policy',      ge: 'ქუქის პოლიტიკა'                  },
  FOOTER_LEGAL_TERMS:         { en: 'Terms of Service',    ge: 'მომსახურების პირობები'             },
  FOOTER_LEGAL_ACCESSIBILITY: { en: 'Accessibility Statement', ge: 'ხელმისაწვდომობის განცხადება'  },

  // ── Cookie consent banner ────────────────────────────────────────────────
  COOKIE_BANNER_TITLE:       { en: 'We use cookies',       ge: 'ვიყენებთ ქუქიებს'                },
  COOKIE_BANNER_BODY:        {
    en: 'We use cookies for authentication and to improve your experience. Read our',
    ge: 'ვიყენებთ ქუქიებს ავთენტიფიკაციისა და გამოცდილების გასაუმჯობესებლად. იხილეთ ჩვენი',
  },
  COOKIE_BANNER_POLICY_LINK: { en: 'Cookie Policy',        ge: 'ქუქიების პოლიტიკა'               },
  COOKIE_BANNER_ACCEPT:      { en: 'Accept',               ge: 'მიღება'                           },
  COOKIE_BANNER_DECLINE:     { en: 'Decline',              ge: 'უარყოფა'                          },
};

/** Pure helper — pass lang explicitly (usable outside React). */
export function t(key: TransKey, lang: Lang): string {
  return T[key]?.[lang] ?? T[key]?.en ?? key;
}

/** React hook — returns a bound t() that reads from the language store. */
export function useT(): (key: TransKey) => string {
  const lang = useLanguageStore((s) => s.lang);
  return (key: TransKey) => t(key, lang);
}